import { z } from "zod";
import { KM_INDEMNITY_RATE_EUR, TASK_TYPES } from "./enums.js";
import {
  ANOMALY_SEVERITIES,
  INVOICE_REVIEW_STATUSES,
  INVOICE_VERDICTS,
  KM_VERDICTS,
  MATCH_CONFIDENCES,
} from "./types/controle-gestion.js";

/**
 * Règle kilométrique Greenkub : le forfait de la mission inclut 200 km
 * aller-retour. Seuls les kilomètres AU-DELÀ sont facturables, au tarif
 * KM_INDEMNITY_RATE_EUR. Un calcul conforme n'est PAS une anomalie.
 */
export const KM_FORFAIT_INCLUDED_ROUNDTRIP_KM = 200;

/** Tolérance sur les montants de prestation, au-delà → à vérifier. */
export const INVOICE_AMOUNT_TOLERANCE_PCT = 5;

/** Tolérance sur les kilomètres quand un itinéraire est joint. */
export const INVOICE_KM_TOLERANCE_PCT = 10;

/** Écart au-delà duquel une estimation de distance devient « suspecte ». */
export const INVOICE_KM_ESTIMATE_SUSPECT_PCT = 25;

// Le modèle renvoie du texte : un nombre peut arriver en string ("372,00 €"),
// ou absent. On normalise ici plutôt que dans chaque consommateur.
const looseNumber = z
  .union([z.number(), z.string(), z.null()])
  .transform((v) => {
    if (v === null || v === "") return null;
    if (typeof v === "number") return Number.isFinite(v) ? v : null;
    const cleaned = v.replace(/[^\d,.-]/g, "").replace(",", ".");
    const n = Number.parseFloat(cleaned);
    return Number.isFinite(n) ? n : null;
  })
  .nullable()
  .default(null);

const looseString = z
  .union([z.string(), z.number(), z.null()])
  .transform((v) => (v === null || v === "" ? null : String(v)))
  .nullable()
  .default(null);

const looseBool = z
  .union([z.boolean(), z.string(), z.null()])
  .transform((v) => (typeof v === "boolean" ? v : v === "true"))
  .default(false);

export const invoiceMatchedTaskSchema = z.object({
  task_id: looseString,
  ref: looseString,
  client: looseString,
  type_mission: z.enum(TASK_TYPES).nullable().catch(null).default(null),
  date_intervention: looseString,
  montant_facture_ht: looseNumber,
  montant_attendu_ht: looseNumber,
  ecart_pct: looseNumber,
  confiance: z.enum(MATCH_CONFIDENCES).catch("douteux").default("douteux"),
  commentaire: looseString,
});

export const invoiceKmLineSchema = z.object({
  libelle: looseString,
  km_factures: looseNumber,
  tarif_eur_km: looseNumber,
  montant_ht: looseNumber,
  aller_retour_km: looseNumber,
  km_attendus: looseNumber,
  montant_attendu_ht: looseNumber,
  source_distance: z
    .enum(["itineraire_joint", "estimation", "inconnue"])
    .catch("inconnue")
    .default("inconnue"),
});

export const invoiceKmControlSchema = z.object({
  verdict: z.enum(KM_VERDICTS).catch("non_verifiable").default("non_verifiable"),
  lignes: z.array(invoiceKmLineSchema).catch([]).default([]),
  commentaire: looseString,
});

export const invoiceFormChecksSchema = z.object({
  numero_present: looseBool,
  date_presente: looseBool,
  emetteur_present: looseBool,
  montants_presents: looseBool,
  franchise_tva: looseBool,
  destinataire_greenkub: looseBool,
});

export const invoiceOffMissionLineSchema = z.object({
  libelle: looseString,
  montant_ht: looseNumber,
  justificatif_joint: looseBool,
});

export const invoiceAnomalySchema = z.object({
  gravite: z.enum(ANOMALY_SEVERITIES).catch("majeure").default("majeure"),
  libelle: z.string().min(1),
});

export const invoiceHeaderSchema = z.object({
  numero: looseString,
  date: looseString,
  emetteur: looseString,
  montant_ht: looseNumber,
  montant_tva: looseNumber,
  montant_ttc: looseNumber,
});

/**
 * Lecture TOLÉRANTE du contrôle rendu par le modèle.
 *
 * Même philosophie que lenient-parse pour les rendus : un champ inattendu ne
 * doit pas faire perdre tout le contrôle. Une analyse partielle affichée, avec
 * ses trous, vaut mieux qu'un écran d'erreur sur une facture qu'un humain doit
 * de toute façon regarder.
 */
export const invoiceAnalysisSchema = z.object({
  decision: z.enum(INVOICE_VERDICTS).catch("a_verifier").default("a_verifier"),
  resume: z.string().catch("").default(""),
  relais_identifie: looseBool,
  relais_nom: looseString,
  facture: invoiceHeaderSchema.catch({
    numero: null,
    date: null,
    emetteur: null,
    montant_ht: null,
    montant_tva: null,
    montant_ttc: null,
  }),
  taches_rapprochees: z.array(invoiceMatchedTaskSchema).catch([]).default([]),
  lignes_hors_mission: z.array(invoiceOffMissionLineSchema).catch([]).default([]),
  controle_km: invoiceKmControlSchema.catch({
    verdict: "non_verifiable",
    lignes: [],
    commentaire: null,
  }),
  controles_forme: invoiceFormChecksSchema.catch({
    numero_present: false,
    date_presente: false,
    emetteur_present: false,
    montants_presents: false,
    franchise_tva: false,
    destinataire_greenkub: false,
  }),
  anomalies: z.array(invoiceAnomalySchema).catch([]).default([]),
});

// ── Payloads d'API ────────────────────────────────────────────────────────

export const invoiceUploadUrlPayloadSchema = z.object({
  file_name: z.string().min(1).max(260),
  relais_id: z.string().uuid().nullable().optional(),
});

/** Pièce justificative jointe au même dépôt que la facture. */
export const invoiceAttachmentPayloadSchema = z.object({
  storage_path: z.string().min(1).max(400),
  file_name: z.string().min(1).max(260),
});

export const invoiceCreatePayloadSchema = z.object({
  storage_path: z.string().min(1).max(400),
  file_name: z.string().min(1).max(260),
  relais_id: z.string().uuid().nullable().optional(),
  // Borné : au-delà d'une dizaine de pièces, le texte joint au contrôle pèse
  // plus que la facture elle-même et noie ce qu'il faut vérifier.
  attachments: z.array(invoiceAttachmentPayloadSchema).max(10).optional(),
});

/** Pièces ajoutées à une facture déjà déposée. */
export const invoiceAttachmentsPayloadSchema = z.object({
  attachments: z.array(invoiceAttachmentPayloadSchema).min(1).max(10),
});

/**
 * Décision de paiement, note, ou les deux.
 *
 * La décision est FACULTATIVE : une note doit pouvoir être ajoutée ou corrigée
 * après coup, sans rejouer la décision — on se souvient rarement de tout
 * justifier au moment où on tranche, et refuser puis expliquer est l'ordre
 * naturel. Au moins l'un des deux champs doit être fourni : un payload vide
 * ne veut rien dire.
 */
export const invoiceReviewPayloadSchema = z
  .object({
    review_status: z
      .enum(
        INVOICE_REVIEW_STATUSES.filter((s) => s !== "pending") as unknown as [
          "accepted",
          "rejected",
        ],
      )
      .optional(),
    note: z.string().max(2000).optional(),
  })
  .refine((v) => v.review_status !== undefined || v.note !== undefined, {
    message: "review_status ou note requis",
  });

export const controlGestionQuerySchema = z.object({
  from: z.string().datetime({ offset: true }).optional(),
  to: z.string().datetime({ offset: true }).optional(),
  relais_ids: z.array(z.string().uuid()).optional(),
});

export const invoiceListQuerySchema = z.object({
  from: z.string().datetime({ offset: true }).optional(),
  to: z.string().datetime({ offset: true }).optional(),
  relais_id: z.string().uuid().optional(),
  verdict: z.enum(INVOICE_VERDICTS).optional(),
  review_status: z.enum(INVOICE_REVIEW_STATUSES).optional(),
});

// ── Aides de calcul (pures, partagées front/back) ─────────────────────────

const round2 = (n: number): number => Math.round(n * 100) / 100;

/**
 * Kilomètres facturables et montant attendu pour un aller-retour donné, selon
 * la règle Greenkub. Un A/R inférieur au forfait ne donne droit à rien.
 */
export function expectedKmBilling(roundTripKm: number): {
  billable_km: number;
  amount_ht: number;
} {
  const billable = Math.max(0, roundTripKm - KM_FORFAIT_INCLUDED_ROUNDTRIP_KM);
  return { billable_km: billable, amount_ht: round2(billable * KM_INDEMNITY_RATE_EUR) };
}

/** Écart relatif en %, null si la référence est absente ou nulle. */
export function ecartPct(actual: number | null, expected: number | null): number | null {
  if (actual === null || expected === null || expected === 0) return null;
  return round2(((actual - expected) / expected) * 100);
}

/**
 * Le contrôle a-t-il levé quelque chose qui mérite un œil humain ?
 * Un verdict « valide » assorti d'anomalies reste signalé : on fait confiance
 * aux faits remontés avant de faire confiance à la conclusion.
 */
export function invoiceNeedsAttention(analysis: {
  decision: string;
  anomalies: { gravite: string }[];
  controle_km: { verdict: string };
} | null): boolean {
  if (!analysis) return true;
  if (analysis.decision !== "valide") return true;
  if (analysis.anomalies.length > 0) return true;
  return analysis.controle_km.verdict === "anomalie" || analysis.controle_km.verdict === "suspect";
}
